import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { lookupPostcode, type AddressSuggestion } from '@/lib/postcode';

interface Address { name?: string; line1?: string; line2?: string; city?: string; postcode?: string; country?: string; phone?: string }
const fmt = (a?: Address | null) => (a?.line1 ? [a.name, a.line1, a.line2, a.city, a.postcode].filter(Boolean).join(', ') : null);

/**
 * Checkout delivery step: choose the saved profile address or enter a new one
 * for this order only (with the option to also save it to the profile).
 */
export function DeliveryAddressBlock({ orderId }: { orderId?: string }) {
  const qc = useQueryClient();
  const { data } = useQuery<{ deliveryAddress?: Address | null }>({
    queryKey: ['profile'],
    queryFn: async () => (await api.get('/me/profile')).data,
  });
  const saved = data?.deliveryAddress ?? null;

  const [mode, setMode] = useState<'saved' | 'new'>('saved');
  const [form, setForm] = useState<Address>({ country: 'United Kingdom' });
  const [saveToProfile, setSaveToProfile] = useState(false);
  const [applied, setApplied] = useState(false);

  // Force the "new address" flow when there's nothing saved to fall back on.
  useEffect(() => { if (!saved) setMode('new'); }, [saved]);

  const apply = useMutation({
    mutationFn: async () => (await api.post('/orders/set-delivery', {
      shippingAddress: form,
      orderId: orderId && orderId !== 'cart' ? orderId : undefined,
      saveToProfile,
    })).data,
    onSuccess: () => {
      toast.success('Delivering to your new address.');
      setApplied(true);
      if (saveToProfile) qc.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not set the address.'),
  });

  const set = (k: keyof Address, v: string) => { setForm({ ...form, [k]: v }); setApplied(false); };

  const [looking, setLooking] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const runLookup = async () => {
    if (!form.postcode || form.postcode.trim().length < 5) { toast.error('Enter a full postcode first.'); return; }
    setLooking(true);
    const r = await lookupPostcode(form.postcode);
    setLooking(false);
    if (!r) { toast('Postcode not found - add your address manually.', { icon: '✍️' }); return; }
    setForm((f) => ({ ...f, city: r.city ?? f.city, country: r.country, postcode: r.postcode }));
    setApplied(false);
    if (r.suggestions.length > 0) {
      setSuggestions(r.suggestions);
      toast.success(`${r.suggestions.length} addresses found - pick yours below.`);
    } else {
      setSuggestions([]);
      toast.success(`${r.city ?? r.region ?? 'Area'} found - now add your street (line 1).`);
    }
  };

  return (
    <div className="card p-4">
      <div className="stamp text-gold-dark text-[9px] mb-2">DELIVERY ADDRESS</div>

      {/* Choice: saved vs new */}
      {saved && (
        <label className={`flex items-start gap-2.5 p-2.5 rounded-luxe border cursor-pointer transition ${mode === 'saved' ? 'border-gold bg-gold/8' : 'border-warmline hover:border-gold/50'}`}>
          <input type="radio" name="addr" className="mt-0.5 accent-gold" checked={mode === 'saved'} onChange={() => setMode('saved')} />
          <span className="min-w-0">
            <span className="text-sm font-medium">Use my saved address</span>
            <span className="block text-[12px] text-mute">{fmt(saved)}{saved.phone ? ` · ${saved.phone}` : ''}</span>
          </span>
        </label>
      )}

      <label className={`flex items-start gap-2.5 p-2.5 rounded-luxe border cursor-pointer transition mt-2 ${mode === 'new' ? 'border-gold bg-gold/8' : 'border-warmline hover:border-gold/50'}`}>
        <input type="radio" name="addr" className="mt-0.5 accent-gold" checked={mode === 'new'} onChange={() => setMode('new')} />
        <span>
          <span className="text-sm font-medium">{saved ? 'Deliver to a different address' : 'Add a delivery address'}</span>
          <span className="block text-[12px] text-mute">Send this order somewhere else.</span>
        </span>
      </label>

      {/* New-address form */}
      {mode === 'new' && (
        <div className="mt-3">
          {applied ? (
            <div className="rounded-luxe border border-ok/40 bg-ok/8 p-3 text-[13px] text-ink">
              ✓ Delivering to: {fmt(form)}
              <button type="button" onClick={() => setApplied(false)} className="block text-[11px] text-gold-dark hover:underline mt-1">Change</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <input className="input col-span-2 text-sm py-1.5" placeholder="Full name" value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} />
              {/* Postcode first: look it up to auto-fill city + country, or type it all manually. */}
              <div className="col-span-2 flex gap-2">
                <input className="input flex-1 text-sm py-1.5 mono uppercase" placeholder="Postcode" value={form.postcode ?? ''}
                  onChange={(e) => set('postcode', e.target.value)}
                  onBlur={() => { if (form.postcode && !form.city) runLookup(); }} />
                <button type="button" onClick={runLookup} disabled={looking} className="btn btn-sm btn-outline shrink-0 whitespace-nowrap">
                  {looking ? 'Finding…' : 'Find address'}
                </button>
              </div>
              {suggestions.length > 0 && (
                <select className="input col-span-2 text-sm py-1.5" defaultValue=""
                  onChange={(e) => { const sug = suggestions[Number(e.target.value)]; if (sug) { setForm((f) => ({ ...f, line1: sug.line1, line2: sug.line2 ?? f.line2, city: sug.city || f.city })); setSuggestions([]); setApplied(false); } }}>
                  <option value="" disabled>Select your address ({suggestions.length} found)…</option>
                  {suggestions.map((sug, i) => <option key={i} value={i}>{sug.label}</option>)}
                </select>
              )}
              <input className="input col-span-2 text-sm py-1.5" placeholder="Address line 1 (street & number)" value={form.line1 ?? ''} onChange={(e) => set('line1', e.target.value)} />
              <input className="input col-span-2 text-sm py-1.5" placeholder="Address line 2 (optional)" value={form.line2 ?? ''} onChange={(e) => set('line2', e.target.value)} />
              <input className="input text-sm py-1.5" placeholder="City / town" value={form.city ?? ''} onChange={(e) => set('city', e.target.value)} />
              <input className="input text-sm py-1.5" placeholder="Country" value={form.country ?? 'United Kingdom'} onChange={(e) => set('country', e.target.value)} />
              <input className="input text-sm py-1.5" placeholder="Phone" value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
              <label className="col-span-2 flex items-center gap-2 text-[12px] text-mute cursor-pointer mt-0.5">
                <input type="checkbox" className="accent-gold" checked={saveToProfile} onChange={(e) => setSaveToProfile(e.target.checked)} />
                Also save this address to my profile
              </label>
              <button
                type="button"
                onClick={() => { if (!form.line1 || !form.city || !form.postcode) { toast.error('Add line 1, city and postcode.'); return; } apply.mutate(); }}
                disabled={apply.isPending}
                className="btn btn-primary btn-sm col-span-2 mt-1"
              >
                {apply.isPending ? 'Saving…' : 'Use this address'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
